import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { confirmEmailChange, verifyEmail } from "../Services/AuthService";
import { formMessage } from "../Services/http";
import Footer from "../Components/Footer";
import "../Styles/Forms.css";

type State = "working" | "done" | "failed";

/**
 * Löser in en verifieringslänk eller en bekräftelse av adressbyte.
 *
 * Samma komponent för båda, eftersom skillnaden är vilken endpoint som
 * anropas och vad kvittot säger.
 *
 * Inlösningen körs en gång. Utan ref:en nedan skulle Reacts StrictMode i
 * utveckling köra effekten två gånger, och det andra anropet skulle möta
 * ett redan förbrukat token och visa ett fel för något som faktiskt gick
 * bra.
 */
export default function VerifyEmail({ mode = "verify" }: { mode?: "verify" | "email-change" }) {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [state, setState] = useState<State>("working");
  const [message, setMessage] = useState<string>();
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    if (!token) {
      setState("failed");
      setMessage("That link is incomplete. Open it from your email again.");
      return;
    }

    const run = mode === "verify" ? verifyEmail(token) : confirmEmailChange(token);

    run
      .then(() => setState("done"))
      .catch((e) => {
        setState("failed");
        setMessage(formMessage(e) ?? "That link is no longer valid.");
      });
  }, [token, mode]);

  const heading =
    mode === "verify" ? "Email address confirmed" : "Email address updated";

  return (
    <main className="auth-page">
      <div className="auth-card">
        {state === "working" && (
          <>
            <h1>Confirming…</h1>
            <p role="status">One moment.</p>
          </>
        )}

        {state === "done" && (
          <>
            <h1>{heading}</h1>
            <p>
              {mode === "verify"
                ? "Thanks — we can reach you about your account now."
                : "Sign in with your new address from now on."}
            </p>
            <Link className="form-link" to="/login">
              Go to sign in
            </Link>
          </>
        )}

        {state === "failed" && (
          <>
            <h1>That link did not work</h1>
            <p role="alert">{message}</p>
            <p>Links can only be used once, and they expire. Request a new one.</p>
            <Link className="form-link" to="/settings">
              Go to settings
            </Link>
          </>
        )}
      </div>
      <Footer />
    </main>
  );
}
